import { Button, CheckIcon, EditIcon, EmptyState, Modal } from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConditionsDuplication } from '../../../hooks/useConditionsDuplication';
import {
  SubjectConstraints,
  subjectConstraintFields,
  useSubjectConstraints,
} from '../../../hooks/useSubjectConstraints';
import { showSuccessSaveToast, showUserErrorToast } from '../../../utils/toast';
import { ConstraintsItem } from '../../Constraints/ConstraintsItem';
import { DuplicateConditionsModal } from '../../Constraints/DuplicateConditionsModal';
import { SubjectConditionsForm } from '../../Constraints/Form/SubjectConditionsForm';
import { ConditionsControlProps, ConstraintsItemType } from '../../Constraints/types';
import { useTimetableStore } from '../../TimetableGrid/TimetableProvider';
import { useVersionConstraints } from '../hooks/useVersionConstraints';

/*
 * Fach-Bedingungen (F8/F6/F15): schreibt den `constraints`-Blob auf die Subject↔Version-Kante,
 * den der Timefold-Export (subjectConstraints → Lesson.maxPerDay/maxDaysPerWeek/preferEdgeOfDay) liest.
 * Roh-urql statt generierter Hooks: die Mutation existiert noch nicht im Schema-Codegen (Offline-Build).
 * Liste + Modal wie bei den Klassen.
 */

type Props = { versionUuid: string; subjectUuid: string; name?: string } & ConditionsControlProps;

export const TimetableVersionSubjectConditions = observer(
  ({
    versionUuid,
    subjectUuid,
    name,
    editOpen,
    setEditOpen,
    duplicateOpen,
    setDuplicateOpen,
    duplicateTargets,
  }: Props) => {
    const { t } = useTranslation();
    const store = useTimetableStore();
    const [duplicateItem, setDuplicateItem] = useState<ConstraintsItemType | null>(null);

    const {
      raw,
      resourceType,
      write: writeConstraints,
      refetch,
    } = useVersionConstraints(versionUuid, 'subject', subjectUuid);

    const constraints: SubjectConstraints = useMemo(() => {
      try {
        return raw ? (JSON.parse(raw) as SubjectConstraints) : {};
      } catch {
        return {};
      }
    }, [raw]);

    const items = useSubjectConstraints(constraints);

    const write = async (targetUuid: string, blob: Record<string, unknown>) => {
      const { error } = await writeConstraints(targetUuid, blob);
      return !error;
    };

    const {
      loading: duplicating,
      duplicateAll,
      duplicateFields,
    } = useConditionsDuplication({
      versionUuid,
      resourceType,
      write,
    });

    async function handleSave(blob: SubjectConstraints): Promise<boolean> {
      if (store.readonly) return false;
      const { error } = await writeConstraints(subjectUuid, blob);
      if (error) {
        showUserErrorToast({ error });
        return false;
      }
      showSuccessSaveToast();
      refetch({ requestPolicy: 'network-only' });
      return true;
    }

    function handleDelete(item: ConstraintsItemType) {
      if (store.readonly) return;
      const next: SubjectConstraints = { ...constraints };
      if (item.type === 'edgeOfDay') {
        delete next.edgeOfDay;
        delete next.edgeOfDayPriority;
      } else {
        const descriptor = subjectConstraintFields.find((d) => d.field === item.type);
        delete next[item.type as 'maxPerDay' | 'maxDaysPerWeek'];
        if (descriptor) delete next[descriptor.priorityField];
      }
      handleSave(next);
    }

    async function handleDuplicateAll(targetUuids: string[]) {
      await duplicateAll(constraints as Record<string, unknown>, targetUuids);
      setDuplicateOpen(false);
      showSuccessSaveToast();
    }

    async function handleDuplicateItem(targetUuids: string[]) {
      if (!duplicateItem) return;
      const descriptor = subjectConstraintFields.find((d) => d.field === duplicateItem.type);
      const fields = descriptor
        ? [descriptor.field, descriptor.priorityField]
        : duplicateItem.type === 'edgeOfDay'
          ? ['edgeOfDay', 'edgeOfDayPriority']
          : [duplicateItem.type];
      await duplicateFields(constraints as Record<string, unknown>, fields, targetUuids);
      setDuplicateItem(null);
      showSuccessSaveToast();
    }

    return (
      <>
        {items.length === 0 ? (
          <EmptyState
            icon={<CheckIcon className='bordered' />}
            size='small'
            iconColor='var(--color-success)'
            title={t('timetableVersion.constraint.noConstraints')}
            padding='xl'
          />
        ) : (
          items.map((item) => (
            <ConstraintsItem
              key={item.type}
              item={item}
              onDelete={handleDelete}
              onDuplicate={(value) => setDuplicateItem(value)}
            />
          ))
        )}

        <div className='tks__row px-6 py-4' style={{ justifyContent: 'center' }}>
          <Button hierarchy='secondary' icon={<EditIcon />} onClick={() => setEditOpen(true)} disabled={store.readonly}>
            {t('constraint.edit')}
          </Button>
        </div>

        <Modal
          isOpen={editOpen}
          onRequestClose={() => setEditOpen(false)}
          shouldCloseOnEsc={false}
          shouldCloseOnOverlayClick={false}
          title={t('conditions.for', { for: name ?? '' })}
        >
          <SubjectConditionsForm constraints={constraints} onSubmit={handleSave} onClose={() => setEditOpen(false)} />
        </Modal>

        <DuplicateConditionsModal
          isOpen={duplicateOpen}
          onClose={() => setDuplicateOpen(false)}
          title={t('conditions.duplicateModal.title')}
          hint={t('conditions.duplicateModal.hint', { name: name ?? '' })}
          options={duplicateTargets}
          isLoading={duplicating}
          onConfirm={handleDuplicateAll}
        />

        <DuplicateConditionsModal
          isOpen={!!duplicateItem}
          onClose={() => setDuplicateItem(null)}
          title={t('conditions.duplicateModal.titleSingle')}
          hint={t('conditions.duplicateModal.hintSingle', { name: name ?? '' })}
          options={duplicateTargets}
          isLoading={duplicating}
          onConfirm={handleDuplicateItem}
        />
      </>
    );
  },
);
